import { getReportFooterText } from '../reportFooter';
export interface SuggestOnHandInventoryHeaderInfo {
  startDate?: string | null;
  endDate?: string | null;
  linFilter?: string | null;
  ieLabel?: string | null;
}

export interface SuggestOnHandInventoryItem {
  lin?: string | null;
  nsn?: string | null;
  nomenclature: string;
  qtyIssued?: number | null;
  unitPrice?: number | null;
  linTotalCost?: number | null;
  year?: number | null;
  monthLabel?: string | null;
}

export interface SuggestOnHandInventoryGrandTotals {
  totalQtyIssued: number;
  totalCost: number | null;
}

export interface SuggestOnHandInventoryTemplateOptions {
  generatedAt: Date;
  header: SuggestOnHandInventoryHeaderInfo;
  items: SuggestOnHandInventoryItem[];
  grandTotals?: SuggestOnHandInventoryGrandTotals | null;
  ieKitLabel?: string;
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const sanitizeText = (value: string | null | undefined): string => {
  const trimmed = value?.toString().trim();
  if (!trimmed) {
    return '—';
  }
  return escapeHtml(trimmed);
};

const formatNumber = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '—';
  }
  return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
};

const formatCurrency = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '—';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(value);
};

const formatDateTime = (value: Date): string =>
  new Intl.DateTimeFormat('en-US', {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(value);

const buildItemsTable = (items: SuggestOnHandInventoryItem[]): string => {
  if (items.length === 0) {
    return `
      <tr>
        <td colspan="8" class="empty-row">
          No suggestion data available yet. Run this report after issue activity has been recorded.
        </td>
      </tr>
    `;
  }

  return items
    .map(
      (item) => `
        <tr>
          <td>${sanitizeText(item.lin)}</td>
          <td>${sanitizeText(item.nsn)}</td>
          <td>${sanitizeText(item.nomenclature)}</td>
          <td class="numeric">${formatNumber(item.qtyIssued ?? null)}</td>
          <td class="numeric">${formatCurrency(item.unitPrice ?? null)}</td>
          <td class="numeric">${formatCurrency(item.linTotalCost ?? null)}</td>
          <td class="numeric">${
            typeof item.year === 'number' && Number.isFinite(item.year)
              ? sanitizeText(String(item.year))
              : '—'
          }</td>
          <td>${sanitizeText(item.monthLabel)}</td>
        </tr>
      `,
    )
    .join('');
};

const buildGrandTotalsRow = (grandTotals: SuggestOnHandInventoryGrandTotals | null | undefined): string => {
  if (!grandTotals) return '';
  return `
    <tfoot>
      <tr class="grand-totals">
        <td colspan="3"><strong>Grand Totals</strong></td>
        <td class="numeric"><strong>${formatNumber(grandTotals.totalQtyIssued)}</strong></td>
        <td></td>
        <td class="numeric"><strong>${formatCurrency(grandTotals.totalCost)}</strong></td>
        <td colspan="2"></td>
      </tr>
    </tfoot>
  `;
};

export const buildSuggestOnHandInventoryReportHtml = ({
  generatedAt,
  header,
  items,
  grandTotals,
  ieKitLabel = 'IE Kit',
}: SuggestOnHandInventoryTemplateOptions): string => {
  const generatedLabel = formatDateTime(generatedAt);
  const startLabel = header.startDate ? sanitizeText(header.startDate) : '—';
  const endLabel = header.endDate ? sanitizeText(header.endDate) : '—';
  const linLabel = header.linFilter ? sanitizeText(header.linFilter) : '—';
  const ieLabel = header.ieLabel ? sanitizeText(header.ieLabel) : '—';
  const itemsTable = buildItemsTable(items);
  const grandTotalsRow = buildGrandTotalsRow(grandTotals);

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>Suggest On Hand Inventory</title>
    <style>
      :root {
        color-scheme: light;
      }

      * {
        box-sizing: border-box;
      }

      body {
        margin: 16px;
        font-family: 'Roboto', 'Helvetica Neue', Arial, sans-serif;
        color: #15263c;
        background: #ffffff;
      }

      header {
        margin-bottom: 8px;
      }

      .title {
        font-size: 20px;
        font-weight: 700;
        margin: 0;
        text-align: center;
      }

      .ie-row {
        margin-top: 12px;
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
      }

      .filter-block {
        min-width: 220px;
      }

      .filter-label {
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: #5c6f85;
        margin-bottom: 2px;
      }

      .filter-value {
        font-size: 14px;
        color: #15263c;
      }

      .generated {
        margin-top: 4px;
        font-size: 12px;
        color: #5c6f85;
        text-align: right;
      }

      table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 8px;
        border: 1px solid #d7e3f4;
      }

      thead th {
        background: #e9f1ff;
        text-align: left;
        font-size: 12px;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        padding: 8px 10px;
        color: #4a5c72;
        border-bottom: 1px solid #d7e3f4;
      }

      tbody td {
        padding: 8px 10px;
        border-bottom: 1px solid #edf3fc;
        font-size: 14px;
        color: #1f2f45;
      }

      tbody tr:last-child td {
        border-bottom: none;
      }

      tbody tr:nth-child(even) td {
        background: #f9fbff;
      }

      .numeric {
        text-align: center;
        font-variant-numeric: tabular-nums;
      }

      .empty-row {
        text-align: center;
        font-style: italic;
        color: #6b7c92;
        padding: 24px 12px;
      }

      tfoot td {
        padding: 10px;
        border-top: 2px solid #d7e3f4;
        background: #e9f1ff;
        font-size: 14px;
        color: #15263c;
      }

      footer {
        margin-top: 16px;
        font-size: 12px;
        color: #5c6f85;
        text-align: center;
      }

      @media print {
        body {
          margin: 8mm;
        }
      }
    </style>
  </head>
  <body>
    <header>
      <h1 class="title">Suggest On Hand Inventory</h1>
      <div class="ie-row">
        <div class="filter-block">
          <div class="filter-label">Start Date</div>
          <div class="filter-value">${startLabel}</div>
        </div>
        <div class="filter-block">
          <div class="filter-label">End Date</div>
          <div class="filter-value">${endLabel}</div>
        </div>
        <div class="filter-block">
          <div class="filter-label">LIN</div>
          <div class="filter-value">${linLabel}</div>
        </div>
        <div class="filter-block">
          <div class="filter-label">${ieKitLabel}</div>
          <div class="filter-value">${ieLabel}</div>
        </div>
      </div>
      <div class="generated">Generated ${escapeHtml(generatedLabel)}</div>
    </header>

    <section>
      <table>
        <thead>
          <tr>
            <th>LIN</th>
            <th>NSN</th>
            <th>Nomenclature</th>
            <th class="numeric">Qty Issued</th>
            <th class="numeric">Unit Price</th>
            <th class="numeric">Total Cost</th>
            <th class="numeric">Year</th>
            <th>Month</th>
          </tr>
        </thead>
        <tbody>
          ${itemsTable}
        </tbody>
        ${grandTotalsRow}
      </table>
    </section>

    <footer>
      Generated ${escapeHtml(generatedLabel)} · ${getReportFooterText()}
    </footer>
  </body>
</html>`;
};
